import { Button, DatePicker, Input } from 'antd';
import Label from '../label';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  AllReviewsProps,
  createQuarterProps,
  updateQuarterProps,
} from '../../types';
import {
  createQuarter,
  getAllQuarters,
  getQuarter,
  updateQuarter,
} from '../../services/reviewsServices';
import { NotificationAlert } from '..';
import { findEndDate } from './helper';

const { TextArea } = Input;

interface Props {
  editID?: string | null;
  setOpen?(val: boolean): void;
  fetchData?(): void;
}

const defaulData: createQuarterProps = {
  Name: '',
  StartDate: '',
  EndDate: '',
  MenteeDeadline: '',
  MentorDeadline: '',
};

const CreateReview = ({ editID, setOpen, fetchData }: Props) => {
  const navigate = useNavigate();
  const [data, setData] = useState<createQuarterProps>({ ...defaulData });
  const [fillData, setfillData] = useState(false);
  const [isSameQuarter, setIsSameQuarter] = useState(false);
  const [loading, setLoading] = useState(false);
  const myRef = useRef<any>();
  const [quarterData, setQuarterData] = useState<AllReviewsProps>();
  const [isChangedorNull, setIsChangedorNull] = useState(true);

  // set dates
  const handleChange = (field: string, value: string | string[]) => {
    if (field === 'EndDate' || field === 'StartDate') setIsSameQuarter(false);

    if (field === 'StartDate') {
      const endDate = findEndDate(value as string);
      setData({
        ...data,
        MenteeDeadline: '',
        MentorDeadline: '',
        EndDate: endDate,
        StartDate: value as string,
      });
    }

    if (field === 'EndDate')
      setData({
        ...data,
        MenteeDeadline: '',
        MentorDeadline: '',
        EndDate: value as string,
      });

    if (field !== 'EndDate' && field !== 'StartDate')
      setData({ ...data, [field]: value });
  };

  const checkIsNullOrChanged = () => {
    if (Object.values(data).some((i) => i === '')) {
      setIsChangedorNull(true);
    } else if (
      data.Name === quarterData?.name &&
      data.StartDate === dayjs(quarterData?.startDate).format('YYYY-MM-DD') &&
      data.EndDate === dayjs(quarterData?.endDate).format('YYYY-MM-DD') &&
      data.MenteeDeadline ===
        dayjs(quarterData?.menteeDeadline).format('YYYY-MM-DD') &&
      data.MentorDeadline ===
        dayjs(quarterData?.mentorDeadline).format('YYYY-MM-DD')
    ) {
      setIsChangedorNull(true);
    } else {
      setIsChangedorNull(false);
    }
  };

  useEffect(() => {
    checkIsNullOrChanged();
  }, [data]);

  // as soon as both dates are set, check for an overlapping cycle
  useEffect(() => {
    if (editID || !data.StartDate || !data.EndDate) return;

    let isCurrent = true;
    getQuarterData(data.StartDate, data.EndDate).then((overlap) => {
      if (!isCurrent) return;
      setIsSameQuarter(!!overlap);
      if (overlap) {
        myRef.current?.call(
          'error',
          'A review cycle already exists for this date range.'
        );
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [data.StartDate, data.EndDate, editID]);

  //get quarter by id
  useEffect(() => {
    const fetchQuartr = async () => {
      setLoading(true);
      const { data, error } = await getQuarter(editID || '', 'hr');
      setLoading(false);
      if (error) return myRef.current?.call('error', error?.message);
      setQuarterData(data);
      setData({
        Name: data?.name || '',
        StartDate: dayjs(data?.startDate).format('YYYY-MM-DD'),
        EndDate: dayjs(data?.endDate).format('YYYY-MM-DD'),
        MenteeDeadline: dayjs(data?.menteeDeadline).format('YYYY-MM-DD'),
        MentorDeadline: dayjs(data?.mentorDeadline).format('YYYY-MM-DD'),
      });
    };
    if (editID) {
      fetchQuartr();
    }
  }, [editID]);

  // check all fields
  const checkValidation = () => {
    let ret = true;
    setfillData(false);
    Object.entries(data).forEach((val) => {
      if (val[1] === '') ret = false;
    });
    return ret;
  };

  const handleSubmit = async () => {
    if (!checkValidation()) {
      return setfillData(true);
    }
    setLoading(true);

    if (!editID) {
      if (await getQuarterData(data.StartDate, data.EndDate)) {
        setIsSameQuarter(true);
        setLoading(false);
        return myRef.current?.call('error', 'Quarter already exists');
      }
    }

    const { error } = editID
      ? await updateQuarter({
          ...(quarterData as updateQuarterProps),
          ...data,
        })
      : await createQuarter(data);
    if (error) {
      setLoading(false);
      return myRef.current?.call('error', error?.message);
    }
    myRef.current?.call('success');

    setTimeout(() => {
      setLoading(false);
      fetchData?.();
      setOpen?.(false);
    }, 400);

    if (!editID) navigate(-1);
  };

  const getQuarterData = async (startDate: string, endDate: string) => {
    const { data, error } = await getAllQuarters('hr');
    if (error) return myRef.current?.call('error', error?.message);

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    const quarter = data.find((i) => {
      const iStartDate = new Date(i.startDate);
      const iEndDate = new Date(i.endDate);

      // Check if the given date range overlaps with the quarter's date range
      return (
        (startDateObj >= iStartDate && startDateObj <= iEndDate) ||
        (endDateObj >= iStartDate && endDateObj <= iEndDate) ||
        (startDateObj <= iStartDate && endDateObj >= iEndDate)
      );
    });

    return quarter;
  };

  return (
    <div>
      <div className="opacity-45">
        {editID
          ? 'Use this form to update the details of a created quarterly review cycle. Once a cycle is initiated you can only update the mentor and mentee deadlines.'
          : 'Set flexible quarterly review cycle start and end dates to align with business cycles while establishing mentor and mentee deadlines to ensure timely feedback and fostering continuous employee development throughout the review period.'}
      </div>
      <div className="mt-10 flex flex-col gap-y-10">
        <div className="grid grid-cols-5 gap-x-[10px]">
          <Label label="Quarterly Review Cycle Name" className="col-span-5">
            <TextArea
              disabled={
                quarterData?.status === 1 ||
                quarterData?.status === 3 ||
                quarterData?.status === 2
                  ? true
                  : false
              }
              style={{ height: 60 }}
              placeholder="Quarterly Review Cycle Name"
              onChange={(e) => handleChange('Name', e.target.value)}
              value={data.Name}
            />
          </Label>
        </div>
        <div className="grid grid-cols-4 gap-x-[10px]">
          <Label label="Start Date" className="">
            <DatePicker
              disabled={
                quarterData?.status === 1 ||
                quarterData?.status === 3 ||
                quarterData?.status === 2
                  ? true
                  : false
              }
              onChange={(_, dateString) =>
                handleChange('StartDate', dateString)
              }
              className="w-full"
              placeholder="Select date"
              value={data.StartDate === '' ? null : dayjs(data.StartDate)}
            />
          </Label>
          <Label label="End Date" className="">
            <DatePicker
              onChange={(_, dateString) => handleChange('EndDate', dateString)}
              className="w-full"
              placeholder="Select date"
              value={data.EndDate === '' ? null : dayjs(data.EndDate)}
              maxDate={dayjs(findEndDate(data.StartDate))}
              disabled={
                quarterData?.status === 1 ||
                quarterData?.status === 3 ||
                quarterData?.status === 2 ||
                data.StartDate === ''
                  ? true
                  : false
              }
            />
          </Label>
          <Label label="Mentee Deadline" className="">
            <DatePicker
              onChange={(_, dateString) =>
                handleChange('MenteeDeadline', dateString)
              }
              className="w-full"
              placeholder="Select date"
              value={
                data.MenteeDeadline === '' ? null : dayjs(data.MenteeDeadline)
              }
              disabled={data.StartDate === '' || data.EndDate === ''}
              // maxDate={dayjs(data.EndDate)}
              minDate={dayjs(data.StartDate)}
            />
          </Label>
          <Label label="Mentor Deadline" className="">
            <DatePicker
              onChange={(_, dateString) =>
                handleChange('MentorDeadline', dateString)
              }
              className="w-full"
              placeholder="Select date"
              value={
                data.MentorDeadline === '' ? null : dayjs(data.MentorDeadline)
              }
              disabled={data.StartDate === '' || data.EndDate === ''}
              // maxDate={dayjs(data.EndDate)}
              minDate={dayjs(data.StartDate)}
            />
          </Label>
        </div>
      </div>
      <div className="flex mt-10">
        {fillData ? (
          <div className="text-[red]">Please fill all the fields</div>
        ) : isSameQuarter ? (
          <div className="text-[red]">
            A review cycle already exists for this date range. Please choose a
            different range.
          </div>
        ) : null}
        <div className="flex gap-x-2 ml-auto">
          <Button
            onClick={() => {
              if (editID) setOpen?.(false);
              else navigate(-1);
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={loading || isChangedorNull || isSameQuarter}
          >
            {!editID ? 'Create New Review Cycle' : 'Update Review Cycle'}
          </Button>
        </div>
      </div>
      <NotificationAlert ref={myRef} />
    </div>
  );
};

export default CreateReview;
